import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, Star } from "lucide-react";
import { MaskLine, Reveal, Stagger, StaggerItem } from "@/components/Motion";
import BookingDialog from "@/components/BookingDialog";
import Seo, { ORG_LD, SITE } from "@/components/Seo";
import { MANIFESTO, WHY, COURSES, EVENTS, IMAGES } from "@/data/content";
import api from "@/lib/api";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen min-h-[700px] overflow-hidden constellation">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.akasha} alt="Akasha" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7EFE0]/60 via-[#F7EFE0]/40 to-[#F7EFE0]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Spiritualità moderna · Formazione Akashica</motion.p>

        <h1 className="font-display text-5xl sm:text-7xl lg:text-[6rem] leading-[0.98] tracking-tight text-[#2A1F14] max-w-5xl">
          <MaskLine delay={0.1}>Dove l'impossibile</MaskLine>
          <MaskLine delay={0.28} className="italic text-gold">diventa possibilità.</MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 text-[#2A1F14]/60 text-lg max-w-xl leading-relaxed">
          Un ecosistema premium dedicato alla crescita personale, alla spiritualità contemporanea e alla formazione olistica. Il cuore è LUX ACADEMY.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link to="/servizi" data-testid="hero-scopri" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-sm font-medium tracking-wide hover:bg-[#D4A056] transition-colors">
            Scopri il tuo percorso <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <BookingDialog
            title="Prenota una consulenza"
            presets={{ type: "consulenza", service: "Consulenza spirituale" }}
            trigger={
              <button data-testid="hero-prenota" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[#2A1F14]/25 text-[#2A1F14] text-sm tracking-wide hover:border-gold hover:text-gold transition-colors">
                Prenota una consulenza
              </button>
            }
          />
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#2A1F14]/30 text-xs tracking-[0.2em] uppercase">Scorri</motion.div>
    </section>
  );
}

function SectionHead({ overline, title, className = "" }) {
  return (
    <Reveal className={className}>
      <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{overline}</p>
      <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#2A1F14] max-w-3xl leading-tight">{title}</h2>
    </Reveal>
  );
}

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [blog, setBlog] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/blog").then((r) => setBlog(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo jsonLd={[ORG_LD, { "@context": "https://schema.org", "@type": "WebSite", name: "LUX AETERNA", url: SITE, inLanguage: "it",
        potentialAction: { "@type": "SearchAction", target: `${SITE}/blog?q={search_term_string}`, "query-input": "required name=search_term_string" } }]} />
      <Hero />

      {/* Marquee */}
      <div className="py-8 border-y border-[#2A1F14]/10 bg-[#F7EFE0]">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {["Registri Akashici", "Crescita Personale", "Lux Academy", "Channeling", "Trattamenti PHYL®", "Spiritualità Moderna", "Meditazione", "Regressione"].map((w, i) => (
            <span key={i} className="font-display italic text-3xl sm:text-4xl text-[#2A1F14]/20 mx-8 flex items-center gap-8">
              {w} <span className="w-1.5 h-1.5 rounded-full bg-gold/50" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Filosofia / Manifesto */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <SectionHead overline="La filosofia" title="Un marchio premium costruito sull'essenza dell'Akasha." className="mb-20" />
        <div className="space-y-px">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-[#2A1F14]/10 group">
                <span className="md:col-span-2 font-display text-5xl text-gold/40 group-hover:text-gold transition-colors duration-500">{m.n}</span>
                <h3 className="md:col-span-4 font-display text-2xl sm:text-3xl text-[#2A1F14]">{m.title}</h3>
                <p className="md:col-span-6 text-[#2A1F14]/55 leading-relaxed self-center">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Percorso Akashico */}
      <section className="relative py-28 lg:py-40 constellation overflow-hidden">
        <div className="absolute inset-0 opacity-60">
          <img src={IMAGES.heroGold} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F7EFE0]/50" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Percorso Akashico</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2A1F14] leading-tight mb-8">L'Akasha al centro di ogni cammino.</h2>
            <p className="text-[#2A1F14]/60 leading-relaxed mb-6">I Registri Akashici custodiscono la memoria della tua anima: talenti, blocchi, relazioni e missione di vita. Il Percorso Akashico ti accompagna dall'apertura alla lettura, fino alla piena padronanza attraverso la LUX ACADEMY.</p>
            <Link to="/academy" data-testid="home-akashico-link" className="inline-flex items-center gap-2 text-gold link-underline">Scopri il Percorso Akashico <ArrowRight size={16} /></Link>
          </div>
          <Stagger className="grid gap-4">
            {["Apertura dei Registri", "Lettura akashica guidata", "Formazione in Academy", "Padronanza e insegnamento"].map((s, i) => (
              <StaggerItem key={i}>
                <div className="glass rounded-xl px-6 py-5 flex items-center gap-4">
                  <span className="font-display text-gold text-xl">0{i + 1}</span>
                  <span className="text-[#2A1F14]/80">{s}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Tema Natale */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28" data-testid="home-natal-section">
        <div className="relative overflow-hidden rounded-3xl bg-[#2A1F14] text-[#F7EFE0] px-8 py-16 sm:px-14 sm:py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full border border-gold/20" />
          <div className="absolute -right-10 -top-10 w-[260px] h-[260px] rounded-full border border-gold/15" />
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Nuovo · Interattivo</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight mb-6">Il tuo Tema Natale, letto come mappa dei tuoi talenti.</h2>
            <p className="text-[#F7EFE0]/65 leading-relaxed mb-8 max-w-xl">Calcolo astronomico preciso di Sole, Luna, Ascendente e pianeti. ARIES rivela cinque talenti, tre doni dell'anima e propone una sessione costruita interamente sul tuo cielo.</p>
            <Link to="/tema-natale" data-testid="home-natal-link" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Rivela i miei talenti <ArrowRight size={16} /></Link>
          </div>
          <div className="relative hidden lg:flex justify-center">
            <div className="w-64 h-64 rounded-full border border-gold/40 flex items-center justify-center">
              <div className="w-44 h-44 rounded-full border border-gold/25 flex items-center justify-center">
                <span className="font-display text-7xl text-gold">{"☉\uFE0E"}</span>
              </div>
              {["♈", "♋", "♎", "♑"].map((g, i) => (
                <span key={g} className="absolute text-gold/70 text-xl" style={{ top: ["-14px", "50%", "auto", "50%"][i], bottom: i === 2 ? "-14px" : "auto", left: ["50%", "auto", "50%", "-12px"][i], right: i === 1 ? "-12px" : "auto", transform: "translate(-50%, -50%)" }}>{g + "\uFE0E"}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Perché Lux Aeterna */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <SectionHead overline="Perché sceglierci" title="Competenza, eleganza e accompagnamento reale." className="mb-16" />
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A1F14]/10 rounded-2xl overflow-hidden">
          {WHY.map((w) => (
            <StaggerItem key={w.title}>
              <div className="bg-[#F7EFE0] p-8 h-full hover:bg-[#EFE3CE] transition-colors duration-500">
                <div className="w-2 h-2 rounded-full bg-gold mb-6" />
                <h3 className="font-display text-xl text-[#2A1F14] mb-3">{w.title}</h3>
                <p className="text-[#2A1F14]/50 text-sm leading-relaxed">{w.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Servizi preview */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-14">
          <SectionHead overline="I servizi" title="Un accompagnamento su misura." />
          <Link to="/servizi" className="hidden sm:inline-flex items-center gap-2 text-gold link-underline whitespace-nowrap">Tutti i servizi <ArrowRight size={16} /></Link>
        </div>
        <Stagger className="grid md:grid-cols-3 gap-4">
          {services.slice(0, 6).map((s) => (
            <StaggerItem key={s.slug}>
              <Link to={`/servizi/${s.slug}`} data-testid={`home-service-${s.slug}`} className="block border border-[#2A1F14]/10 rounded-xl p-7 h-full hover:border-gold/40 transition-colors duration-500 group">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-4">{s.category}</p>
                <h3 className="font-display text-2xl text-[#2A1F14] mb-2">{s.title}</h3>
                <p className="text-[#2A1F14]/45 text-sm leading-relaxed mb-6">{s.subtitle}</p>
                <span className="text-[#2A1F14]/40 group-hover:text-gold transition-colors inline-flex items-center gap-2 text-sm">Approfondisci <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Academy */}
      <section className="relative py-28 lg:py-40 bg-[#EFE3CE]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                <img src={IMAGES.academy} alt="Lux Academy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#EFE3CE] to-transparent" />
                <span className="absolute bottom-8 left-8 font-display text-3xl text-[#2A1F14]">LUX ACADEMY</span>
              </div>
            </Reveal>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Il cuore del brand</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#2A1F14] leading-tight mb-8">Una prestigiosa accademia della spiritualità.</h2>
              <p className="text-[#2A1F14]/60 leading-relaxed mb-10">Un catalogo che ruota attorno ai Registri Akashici, articolato in tre livelli progressivi e in percorsi complementari. Online oppure in corsi residenziali immersivi.</p>
              <div className="space-y-3 mb-10">
                {COURSES.filter((c) => c.track === "principale").map((c) => (
                  <Link key={c.slug} to={`/academy/${c.slug}`} className="flex items-center justify-between border-b border-[#2A1F14]/10 py-4 group">
                    <span className="text-[#2A1F14]/80 group-hover:text-gold transition-colors">{c.title}</span>
                    <ArrowRight size={16} className="text-[#2A1F14]/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
              <Link to="/academy" data-testid="home-academy-link" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Esplora l'Academy <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Eventi */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <div className="flex items-end justify-between mb-14">
          <SectionHead overline="Eventi" title="Esperienze dal vivo e immersive." />
          <Link to="/eventi" className="hidden sm:inline-flex items-center gap-2 text-gold link-underline whitespace-nowrap">Tutti gli eventi <ArrowRight size={16} /></Link>
        </div>
        <Stagger className="grid md:grid-cols-3 gap-4">
          {EVENTS.slice(0, 3).map((e) => (
            <StaggerItem key={e.slug}>
              <Link to="/eventi" className="block border border-[#2A1F14]/10 rounded-xl p-7 h-full hover:border-gold/40 transition-colors duration-500">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{e.type}</span>
                  <span className="text-[#2A1F14]/40 text-xs">{e.price}</span>
                </div>
                <h3 className="font-display text-xl text-[#2A1F14] mb-4 leading-snug">{e.title}</h3>
                <p className="text-[#2A1F14]/40 text-sm">{e.date}</p>
                <p className="text-[#2A1F14]/40 text-sm">{e.location}</p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Testimonianze */}
      <section className="bg-[#EFE3CE] py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHead overline="Testimonianze" title="Storie di trasformazione." className="mb-16" />
          <Stagger className="grid md:grid-cols-3 gap-5">
            {testimonials.slice(0, 6).map((t, i) => (
              <StaggerItem key={i}>
                <div className="glass rounded-xl p-7 h-full">
                  <div className="flex gap-1 mb-5">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} size={14} className="fill-gold text-gold" />)}</div>
                  <p className="text-[#2A1F14]/80 leading-relaxed mb-6 font-display italic text-lg">"{t.text}"</p>
                  <p className="text-[#2A1F14] text-sm">{t.name}</p>
                  <p className="text-gold text-xs tracking-wide">{t.role}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Blog */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <div className="flex items-end justify-between mb-14">
          <SectionHead overline="Blog" title="Il riferimento nazionale della spiritualità." />
          <Link to="/blog" className="hidden sm:inline-flex items-center gap-2 text-gold link-underline whitespace-nowrap">Tutti gli articoli <ArrowRight size={16} /></Link>
        </div>
        <Stagger className="grid md:grid-cols-3 gap-4">
          {blog.slice(0, 3).map((b) => (
            <StaggerItem key={b.slug}>
              <Link to={`/blog/${b.slug}`} className="block group">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-3">{b.category}</p>
                <h3 className="font-display text-2xl text-[#2A1F14] mb-3 group-hover:text-gold transition-colors leading-snug">{b.title}</h3>
                <p className="text-[#2A1F14]/45 text-sm leading-relaxed mb-4">{b.excerpt}</p>
                <p className="text-[#2A1F14]/30 text-xs">{b.date} · {b.author}</p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Contatti CTA */}
      <section className="relative py-32 constellation overflow-hidden">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-6">Inizia oggi</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#2A1F14] leading-tight mb-10">Il tuo percorso di trasformazione<br />comincia da una conversazione.</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <BookingDialog title="Prenota una consulenza" presets={{ type: "consulenza", service: "Consulenza spirituale" }}
                trigger={<button data-testid="cta-prenota" className="px-8 py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Prenota una consulenza</button>} />
              <Link to="/contatti" className="px-8 py-4 rounded-full border border-[#2A1F14]/25 text-[#2A1F14] text-sm hover:border-gold hover:text-gold transition-colors">Contattaci</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
